import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import DSPButtons from "./DSPButtons";
import ShareButton from "./ShareButton";
import { Reveal } from "./Reveal";

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

export default function FeaturedRelease({ release }) {
  const { playRelease, currentRelease, isPlaying } = usePlayer();
  if (!release) return null;
  const active = currentRelease?.internalId === release.internalId;

  return (
    <section data-testid="featured-release" className="relative px-6 py-20 sm:px-10 sm:py-28 lg:px-16">
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
        {/* Spotlight artwork */}
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-6 rounded-full bg-gold/20 blur-3xl" />
            <motion.div
              whileTap={{ scale: 0.98 }}
              className="relative aspect-square max-h-[62vh] overflow-hidden rounded-3xl border border-white/10"
            >
              <img
                src={release.artwork}
                alt={`${release.title} cover artwork`}
                className="h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
              <button
                data-testid="featured-play-button"
                onClick={() => playRelease(release)}
                aria-label={`Play ${release.title}`}
                className="absolute bottom-5 right-5 flex h-16 w-16 items-center justify-center rounded-full bg-gold text-black shadow-xl transition-transform active:scale-90 hover:scale-105"
              >
                <Play size={26} fill="currentColor" className="ml-0.5" />
              </button>
            </motion.div>
          </div>
        </Reveal>

        {/* Info */}
        <Reveal delay={0.1}>
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="rounded-full bg-gold px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-black">
                {release.isNew ? "New Release" : "Latest Release"}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-widest text-white/40">
                {release.releaseType}
              </span>
            </div>

            <h2 className="font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-6xl">
              {release.title}
            </h2>
            <p className="mt-3 text-lg text-gold">DJ Código</p>
            <p className="mt-1 text-sm text-white/40">{formatDate(release.releaseDate)}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="featured-listen-now"
                onClick={() => playRelease(release)}
                className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wide text-black transition-transform active:scale-95 hover:bg-gold-2"
              >
                <Play size={18} fill="currentColor" />
                {active && isPlaying ? "Now Playing" : "Listen Now"}
              </button>
              <ShareButton release={release} />
            </div>

            <div className="mt-6">
              <DSPButtons release={release} />
            </div>

            <Link
              to={`/music/${release.slug}`}
              data-testid="featured-view-page"
              className="mt-6 inline-block text-xs font-semibold uppercase tracking-widest text-white/50 underline underline-offset-4 transition-colors hover:text-gold"
            >
              View release page →
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
